import { useQueryClient } from '@tanstack/react-query';
import { FeatureTypeApiModel } from '../../../../types/models/feature-type.model';
import { CreateFeatureTypeApiRequest } from '../../../../types/requests/feature-types/create-feature-type.request';
import { DeleteFeatureTypeApiRequest } from '../../../../types/requests/feature-types/delete-feature-type.request';
import { GetFeatureTypeApiRequest } from '../../../../types/requests/feature-types/get-feature-type.request';
import { SearchFeatureTypesApiRequest } from '../../../../types/requests/feature-types/search-feature-types.request';
import { UpdateFeatureTypeApiRequest } from '../../../../types/requests/feature-types/update-feature-type.request';
import {
  createFeatureType,
  deleteFeatureType,
  getFeatureType,
  searchFeatureTypes,
  updateFeatureType,
} from '../../api/feature-types.api';
import { useAxiosInfiniteQuery, useAxiosMutation, useAxiosQuery } from './use-axios.hook';

const baseQueryKey = 'featureType';
const searchQueryKey = 'search';

export const useCreateFeatureType = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: CreateFeatureTypeApiRequest) => (await createFeatureType(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === searchQueryKey,
      }),
  });
  return [request, { ...result, featureType: data }] as const;
};

export const useUpdateFeatureType = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: UpdateFeatureTypeApiRequest) => (await updateFeatureType(params)).data,
    onSuccess: (featureType) =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          queryKey[0] === baseQueryKey &&
          (queryKey[1] === searchQueryKey ||
            (queryKey[1] as GetFeatureTypeApiRequest)?.featureTypeId === featureType.id),
      }),
  });
  return [request, { ...result, featureType: data }] as const;
};

export const useGetFeatureType = <T extends FeatureTypeApiModel>(params: GetFeatureTypeApiRequest) => {
  const { data, ...result } = useAxiosQuery<T>({
    queryKey: [baseQueryKey, params],
    queryFn: async () => (await getFeatureType<T>(params)).data,
    enabled: Boolean(params.featureTypeId && !Number.isNaN(params.featureTypeId)),
  });
  return { ...result, featureType: data } as const;
};

export const useSearchFeatureTypes = (params: SearchFeatureTypesApiRequest) => {
  const { data, ...result } = useAxiosInfiniteQuery({
    queryKey: [baseQueryKey, searchQueryKey, params],
    queryFn: async ({ pageParam }) => (await searchFeatureTypes({ cursor: pageParam, ...params })).data,
    initialPageParam: params.cursor,
    getNextPageParam: (lastResponse) => lastResponse.nextCursor,
  });
  return { ...result, response: data } as const;
};

export const useDeleteFeatureType = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: DeleteFeatureTypeApiRequest) => (await deleteFeatureType(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === searchQueryKey,
      }),
  });
  return [request, result] as const;
};
